import { map } from 'lodash';
import { isAfter, parseISO } from 'date-fns';
import { ActivatedRoute } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { fromEvent, interval, merge, Subject, Subscription, take, takeUntil } from 'rxjs';
import { MobileProfileService, MobileQuestsService, MobileReputationService, MobileThemeService } from '@sifca-monorepo/clients';
import { Input, Inject, NgZone, Component, ViewChild, ElementRef, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { QuestType, QuestPerformedType } from '@sifca-monorepo/widget-generator';

import { reputationPicture } from '../../environments/environment';

interface QuestWithComingSoon extends QuestType {
  comingSoon: boolean;
}

interface Countdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

@Component({
  template: '',
})
export class MobileQuestsListComponent {
  protected destroy$: Subject<any> = new Subject<any>();

  @Input() param: string;
  @Input() showFilter = true;
  @Input() limit: number = 1000;
  @Input() showFeatured: boolean = true;
  @Input() quests: (QuestType | QuestPerformedType)[] = [];
  @Input() gridClasses = 'grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';

  @ViewChild('questsPreloader', { static: false }) questsPreloaderEl!: ElementRef;

  public countdownTick: number = 0;
  protected questUserInteracted = false;
  protected countdownTickerSub?: Subscription;
  protected questInteractionSub!: Subscription;
  protected questsPreloaderObserver?: IntersectionObserver;
  public questCountdowns: { [id: string]: Countdown } = {};

  posId: string;
  isQuestsLoading: boolean;
  activeTab = 'allQuests';
  selectedCategories: string[] = [];
  selectedQuestCategories: any[] = [];
  dark$ = this.themeService.darkMode$;
  reputationPicture = reputationPicture;
  initialQuests$ = this.questsService.initialQuests$;
  loadingQuests$ = this.questsService.loadingQuests$;
  loyaltySettings$ = this.reputationService.loyaltySettings$;

  // Modal local state to avoid applying changes until user clicks Apply
  public modalDirty: boolean = false;
  public modalSortSelection: string = 'true';

  /**
   * Set when a user-initiated reset clears quests$ so the subscription can tell that apart
   * from a sibling constructor wiping the shared BehaviorSubject mid-fetch.
   */
  protected pendingUserReset = false;

  constructor(
    protected ngZone: NgZone,
    protected cd: ChangeDetectorRef,
    protected activatedRoute: ActivatedRoute,
    protected themeService: MobileThemeService,
    protected questsService: MobileQuestsService,
    protected profileService: MobileProfileService,
    protected reputationService: MobileReputationService,
    @Inject(PLATFORM_ID) protected platformId: any,
  ) {
    this.questsService.isLast$ = false;
    this.questsService.loadingActivitiesByQuest$ = false;
  }

  protected mapQuestsWithComingSoon(quests: (QuestType | QuestPerformedType)[]): (QuestType | QuestPerformedType)[] {
    return map(quests, (quest: QuestType | QuestPerformedType) => {
      let comingSoon = false;
      if (quest?.startDate) {
        try {
          const parsed = parseISO(quest.startDate);
          if (!isNaN(parsed as any)) {
            comingSoon = isAfter(parsed, new Date());
          }
        } catch {
          comingSoon = false;
        }
      }

      return {
        ...quest,
        comingSoon,
      } as any;
    });
  }

  ngOnInit(): void {
    if (this.param === 'referral') {
      this.posId = this.activatedRoute.snapshot.paramMap.get('id');
      this.profileService.pointOfSale(this.posId).subscribe();
    }
    this.questsService.quests$.pipe(takeUntil(this.destroy$)).subscribe((quests) => {
      if (quests?.length) {
        const mappedQuests = this.mapQuestsWithComingSoon(quests);
        const wasReset = this.pendingUserReset;
        this.pendingUserReset = false;

        if (wasReset || this.questsService.pageIndex === 0) {
          this.quests = mappedQuests;
        } else {
          const existingIds = new Set(this.quests.map((q) => q.id));
          const incoming = mappedQuests.filter((q) => !existingIds.has(q.id));
          if (incoming.length) {
            this.quests.push(...incoming);
          }
        }
      } else if (quests === null && !this.pendingUserReset) {
        // quests$ was cleared by another instance, not by an intentional user reset.
        // Keep the current list while a fetch is in-flight to avoid a false empty state.
        let currentlyLoading = false;
        this.questsService.loadingQuests$.pipe(take(1)).subscribe((v) => (currentlyLoading = !!v));
        if (!currentlyLoading) {
          this.quests = [];
        }
      } else {
        if (quests !== null) {
          this.pendingUserReset = false;
        }
        this.quests = [];
      }
      this.updateAllCountdowns();
      this.cd.markForCheck();
    });
    this.startCountdownTicker();
  }

  ngAfterViewInit() {
    // Products infinite scroll
    if (!isPlatformBrowser(this.platformId)) return;

    this.ngZone.runOutsideAngular(() => {
      const scroll$ = fromEvent(window, 'scroll');
      const wheel$ = fromEvent(window, 'wheel');
      const touch$ = fromEvent(window, 'touchstart');
      this.questInteractionSub = merge(scroll$, wheel$, touch$)
        .pipe(take(1))
        .subscribe(() => {
          this.questUserInteracted = true;
          this.setupQuestsPreloaderObserver();
        });
    });
  }

  startCountdownTicker() {
    this.ngZone.runOutsideAngular(() => {
      this.countdownTickerSub = interval(1000)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.countdownTick++;
          this.updateAllCountdowns();
          this.ngZone.run(() => this.cd.markForCheck());
        });
    });
  }

  updateAllCountdowns() {
    if (!this.quests) return;
    for (const quest of this.quests) {
      this.questCountdowns[quest.id] = this.getCountdown(quest as QuestWithComingSoon);
      this.cd.markForCheck();
    }
  }

  getDueCountdown(quest: QuestType | QuestPerformedType): Countdown {
    if (!quest?.dueDate) {
      return this.emptyCountdown();
    }
    return this.calculateDifference(quest.dueDate);
  }

  getComingCountdown(quest: QuestType | QuestPerformedType): Countdown {
    if (!quest?.startDate) {
      return this.emptyCountdown();
    }
    return this.calculateDifference(quest.startDate);
  }

  getCountdown(quest: QuestWithComingSoon): Countdown {
    if (quest?.comingSoon) {
      return this.getComingCountdown(quest);
    } else {
      return this.getDueCountdown(quest);
    }
  }

  calculateDifference(dateString: string): Countdown {
    try {
      const target = parseISO(dateString);
      const now = new Date();

      if (!isAfter(target, now)) {
        return this.emptyCountdown();
      }

      const difference = target.getTime() - now.getTime();
      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
      };
    } catch {
      return this.emptyCountdown();
    }
  }

  emptyCountdown(): Countdown {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    };
  }

  onChangeQuests(event: string) {
    this.pendingUserReset = true;
    if (event === 'comingSoon') {
      this.activeTab = 'comingSoon';
    } else if (event === 'allQuests') {
      this.activeTab = 'allQuests';
      this.questsService.performed = null;
    } else if (event === 'completed') {
      this.activeTab = 'completed';
      this.questsService.performed = true;
    } else if (event === 'uncompleted') {
      this.activeTab = 'uncompleted';
      this.questsService.performed = false;
    } else if (event === 'featured') {
      this.activeTab = 'featured';
      this.quests = [];
      this.questsService.pageIndex = 0;
      this.questsService.quests$ = null;
      this.cd.markForCheck();
      return;
    }
    this.cd.markForCheck();
    this.applyFilter();
  }

  sortQuests(event: string) {
    this.questsService.reversed = event === 'false' ? false : true;
  }

  openFilterModal() {
    // Initialize modal state from current service values
    this.modalSortSelection = String(this.questsService.reversed);
    this.modalDirty = false;
  }

  updateModalSort(value: string) {
    this.modalSortSelection = value;
    this.modalDirty = value !== String(this.questsService.reversed);
  }

  applyModalChanges() {
    if (!this.modalDirty) return;
    this.sortQuests(this.modalSortSelection);
    this.applyFilter();
    this.modalDirty = false;
  }

  applyFilter() {
    this.pendingUserReset = true;
    this.quests = [];
    this.questsService.pageIndex = 0;
    this.questsService.quests$ = null;
    if (this.param === 'referral') {
      this.fetchReferralQuests();
    } else {
      this.fetchBaseQuests();
    }
  }

  setupQuestsPreloaderObserver() {
    if (!this.questsPreloaderEl) {
      setTimeout(() => this.setupQuestsPreloaderObserver(), 150);
      return;
    }
    if (this.questsPreloaderObserver) {
      this.questsPreloaderObserver.disconnect();
    }
    const options: IntersectionObserverInit = {
      root: null,
      threshold: 0.1,
      rootMargin: '0px 0px 150px 0px',
    };
    this.questsPreloaderObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && this.questUserInteracted && !this.isQuestsLoading) {
          this.ngZone.run(() => {
            this.questsService.isLast$.pipe(take(1)).subscribe((isLast) => {
              if (isLast === false && this.limit > 5) {
                this.loadMoreQuests();
              } else {
                this.questsPreloaderObserver?.disconnect();
              }
            });
          });
        }
      });
    }, options);
    this.questsPreloaderObserver.observe(this.questsPreloaderEl.nativeElement);
  }

  fetchReferralQuests(reset = true) {
    if (this.activeTab === 'comingSoon') {
      this.questsService.getComingSoonQuestsByTargetAndUserAudiencePaginated(this.posId, reset).subscribe(() => {
        this.isQuestsLoading = false;
        this.cd.markForCheck();
      });
    } else {
      this.questsService.getQuestsByTargetAndUserAudiencePaginated(this.posId, reset).subscribe(() => {
        this.isQuestsLoading = false;
        this.cd.markForCheck();
      });
    }
  }

  fetchBaseQuests(reset = true) {
    if (this.activeTab === 'comingSoon') {
      this.questsService.getComingSoonQuestsByTargetAndUserAudienceWithLinkedAccountsPaginated(reset).subscribe(() => {
        this.isQuestsLoading = false;
        this.cd.markForCheck();
      });
    } else {
      this.questsService.getQuestsByTargetAndUserAudienceWithLinkedAccountsPaginated(reset).subscribe(() => {
        this.isQuestsLoading = false;
        this.cd.markForCheck();
      });
    }
  }

  loadMoreQuests() {
    this.isQuestsLoading = true;
    this.questsService.pageIndex++;
    if (this.param === 'referral') {
      this.fetchReferralQuests(false);
    } else {
      this.fetchBaseQuests(false);
    }
  }

  resetQuests() {
    this.quests = [];
    this.questCountdowns = {};
    this.questsService.pageIndex = 0;
    this.questsService.reversed = false;
    this.questsService.performed = null;
    this.questsService.quests$ = null;
  }

  ngOnDestroy(): void {
    this.resetQuests();
    this.destroy$.next(null);
    this.destroy$.complete();
    if (this.questInteractionSub) this.questInteractionSub.unsubscribe();
    if (this.countdownTickerSub) this.countdownTickerSub.unsubscribe();
    if (this.questsPreloaderObserver) this.questsPreloaderObserver.disconnect();
  }
}
